import { useState } from 'react';
import { Input } from '@/components/ui/input';
import type { OnboardingDraft } from '../useOnboardingStore';
import {
  Eyebrow,
  FieldLabel,
  Heading,
  Lede,
  Note,
  PrimaryButton,
  QuietLink,
  SecondaryButton,
  StatusRow,
  StepBody,
  StepFooter,
} from '../OnboardingShell';

function BackFooter({
  children,
  onHub,
}: {
  children: React.ReactNode;
  onHub: () => void;
}) {
  return (
    <StepFooter>
      {children}
      <SecondaryButton onClick={onHub}>Back to your list</SecondaryButton>
    </StepFooter>
  );
}

export function InviteTeam({
  draft,
  busy,
  onSend,
  onJustMe,
  onHub,
}: {
  draft: OnboardingDraft;
  busy: boolean;
  onSend: (email: string) => void;
  onJustMe: () => void;
  onHub: () => void;
}) {
  const [email, setEmail] = useState('');
  return (
    <>
      <StepBody>
        <Eyebrow>Your team</Eyebrow>
        <Heading>Invite your team</Heading>
        <Lede>
          Only people you name get a login. Just you is fine — you already have access.
        </Lede>
        <FieldLabel htmlFor="onboarding-invite">Email</FieldLabel>
        <Input
          id="onboarding-invite"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="name@yourbusiness.com"
        />
        {draft.inviteEmails.length > 0 && (
          <div className="mt-4">
            {draft.inviteEmails.map((sent) => (
              <StatusRow key={sent} tone="done" title={sent} detail="Invite sent" />
            ))}
          </div>
        )}
      </StepBody>
      <StepFooter>
        <PrimaryButton
          onClick={() => onSend(email)}
          disabled={busy || !email.trim()}
        >
          {busy ? 'Sending…' : 'Send invite'}
        </PrimaryButton>
        <SecondaryButton onClick={onJustMe} disabled={busy}>
          Just me for now
        </SecondaryButton>
        <SecondaryButton onClick={onHub} disabled={busy}>
          Back to your list
        </SecondaryButton>
      </StepFooter>
    </>
  );
}

export function ProveSite({
  draft,
  savedKeyPresent,
  busy,
  onChange,
  onProve,
  onHub,
}: {
  draft: OnboardingDraft;
  savedKeyPresent: boolean;
  busy: boolean;
  onChange: (next: Partial<OnboardingDraft>) => void;
  onProve: () => void;
  onHub: () => void;
}) {
  const hasKey = Boolean(draft.pluginKey.trim()) || savedKeyPresent;
  const proved = Boolean(draft.siteUrl);

  if (proved && draft.siteUrl) {
    return (
      <>
        <StepBody>
          <Eyebrow>The site</Eyebrow>
          <Heading>The site is active.</Heading>
          <Lede>
            The plugin key check passed. That's the proof — saving the key alone is not
            enough.
          </Lede>
          <StatusRow tone="done" title="Site" detail={draft.siteUrl} />
        </StepBody>
        <BackFooter onHub={onHub}>
          <PrimaryButton onClick={onHub}>Back to your list</PrimaryButton>
        </BackFooter>
      </>
    );
  }

  if (!hasKey) {
    return (
      <>
        <StepBody>
          <Eyebrow>The site</Eyebrow>
          <Heading>Waiting for the plugin key.</Heading>
          <Lede>
            Paste the key from WP Settings → AgileCO Platform. We use it to check the site is
            active.
          </Lede>
          <FieldLabel htmlFor="onboarding-prove-key">Plugin key</FieldLabel>
          <Input
            id="onboarding-prove-key"
            value={draft.pluginKey}
            onChange={(e) => onChange({ pluginKey: e.target.value, pluginSkipped: false })}
            placeholder="sk_agileco_…"
            autoComplete="off"
          />
          <p className="mt-3">
            <Note>
              Upload the zip in wp-admin — your contact sent it. We don't host a download
              here. Skip this job until you have access — go-live needs this check.
            </Note>
          </p>
          <div className="mt-4">
            <QuietLink href="/settings/integrations">Open WordPress connection</QuietLink>
          </div>
        </StepBody>
        <BackFooter onHub={onHub}>
          <PrimaryButton onClick={onProve} disabled={busy || !draft.pluginKey.trim()}>
            {busy ? 'Checking…' : 'Save key and check the site is active'}
          </PrimaryButton>
        </BackFooter>
      </>
    );
  }

  return (
    <>
      <StepBody>
        <Eyebrow>The site</Eyebrow>
        <Heading>Check the site is active</Heading>
        <Lede>
          One request with your plugin key confirms the site is active. This is the real
          check — a saved key alone is not proof.
        </Lede>
        <StatusRow
          tone="pending"
          title="Plugin key"
          detail={draft.pluginKey.trim() ? 'Pasted — not proven yet' : 'Saved — not proven yet'}
        />
      </StepBody>
      <BackFooter onHub={onHub}>
        <PrimaryButton onClick={onProve} disabled={busy}>
          {busy ? 'Checking…' : 'Check the site is active'}
        </PrimaryButton>
      </BackFooter>
    </>
  );
}

const FOUNDATION_ITEMS = [
  {
    id: 'profile',
    title: 'Your business profile',
    detail: 'Who you are and how you sound',
    href: '/brand-bible',
  },
  {
    id: 'prefs',
    title: 'How we write',
    detail: 'Length, voice, what to avoid',
    href: '/settings/content',
  },
  {
    id: 'locations',
    title: 'Locations on the site',
    detail: 'The places you serve',
    href: '/seo/schema-management',
  },
  {
    id: 'calendar',
    title: 'What to write first',
    detail: 'Topics for the first articles',
    href: '/content/planner',
  },
] as const;

export function Foundation({
  draft,
  onToggle,
  onDone,
  onHub,
}: {
  draft: OnboardingDraft;
  onToggle: (id: string) => void;
  onDone: () => void;
  onHub: () => void;
}) {
  const allOn = FOUNDATION_ITEMS.every((item) => draft.foundationDone.includes(item.id));
  return (
    <>
      <StepBody>
        <Eyebrow>Your business profile</Eyebrow>
        <Heading>Four things, in order</Heading>
        <Lede>
          Each one opens the real page. Mark it done here when you've started it. We don't
          duplicate those records.
        </Lede>
        {FOUNDATION_ITEMS.map((item) => {
          const done = draft.foundationDone.includes(item.id);
          return (
            <div key={item.id} className="mb-2.5">
              <StatusRow
                tone={done ? 'done' : 'pending'}
                title={item.title}
                detail={item.detail}
                onClick={() => onToggle(item.id)}
              />
              <p className="-mt-1 mb-2 pl-9">
                <a
                  href={item.href}
                  className="text-[13px] font-medium text-teal underline underline-offset-4 hover:text-teal-dark"
                >
                  Open
                </a>
              </p>
            </div>
          );
        })}
      </StepBody>
      <BackFooter onHub={onHub}>
        <PrimaryButton onClick={onDone} disabled={!allOn}>
          Mark this job done
        </PrimaryButton>
      </BackFooter>
    </>
  );
}

export function BaselineCrawl({ onDone, onHub }: { onDone: () => void; onHub: () => void }) {
  return (
    <>
      <StepBody>
        <Eyebrow>First check</Eyebrow>
        <Heading>Run the first site check</Heading>
        <Lede>
          This run is the baseline. Later checks compare to this one, not to an empty start.
        </Lede>
        <StatusRow
          tone="pending"
          title="Site check"
          detail="Opens the existing check page. This list only records that you started it."
        />
        <p className="mt-1">
          <a
            href="/audit/technical"
            className="text-[13px] font-medium text-teal underline underline-offset-4 hover:text-teal-dark"
          >
            Open site check
          </a>
        </p>
      </StepBody>
      <BackFooter onHub={onHub}>
        <PrimaryButton onClick={onDone}>I've started the check</PrimaryButton>
      </BackFooter>
    </>
  );
}

export function AdsLink({ onDone, onHub }: { onDone: () => void; onHub: () => void }) {
  return (
    <>
      <StepBody>
        <Eyebrow>Paid search</Eyebrow>
        <Heading>Map the Ads account</Heading>
        <Lede>
          Mapping happens on the connections page. This list records that it's done — it does
          not create the mapping itself.
        </Lede>
        <StatusRow
          tone="pending"
          title="Google Ads"
          detail="Open connections, then come back and mark this done."
        />
        <p className="mt-1">
          <a
            href="/settings/integrations"
            className="text-[13px] font-medium text-teal underline underline-offset-4 hover:text-teal-dark"
          >
            Open connections
          </a>
        </p>
      </StepBody>
      <BackFooter onHub={onHub}>
        <PrimaryButton onClick={onDone}>This is mapped</PrimaryButton>
      </BackFooter>
    </>
  );
}
